import { AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import ApiPath from "src/api/apiPath.json";
import { ImageRequestModal } from "src/types/product";
import { ApiResponse, MediaFile, Sort, UnionCmdType } from "../public-types";
import {
  ClosingRecurrenceAddParams,
  ClosingRecurrenceDeleteParams,
  ClosingRecurrenceEditParams,
  InternalMemberContractMap,
  ProductAddModel,
  ProductBuildingCommonFacilitySaveListReq,
  ProductBuildingCommonFacilitySaveListRes,
  ProductDetailType,
  ProductEditModel,
  ProductListData,
  ProductListParams,
  ProductMediaFileList,
  ProductUpdateResponseData,
  ReservationPolicyExclusiveParams,
  ReservationPolicyListParams,
  ReservationPolicyModel,
  ReservationPolicyOwner,
  ReservationPolicyPage,
  ReservationPolicySaveParams,
  ReservationPolicyTarget,
} from "./product-types";
import { ProductStatusT, SpaceTypeT } from "src/pages/product/product-types";
import { BuildingModel } from "../building/building-types";
import { UnionServiceType } from "../adminMemo/adminmemo-types";

// RPP01. 공간상품 기본정보 목록
export async function getProductListAsync(axios: AxiosInstance, params?: ProductListParams): Promise<AxiosResponse<ApiResponse<ProductListData>>> {
  return await axios.get<ApiResponse<ProductListData>>(ApiPath.product.list, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

// RPP02. 공간상품 기본정보 상태 변경

export async function productStatusAsync(
  axios: AxiosInstance,
  params?: {
    productId: number;
    status: ProductStatusT;
  },
): Promise<AxiosResponse<ApiResponse<ProductUpdateResponseData>>> {
  const path = ApiPath.product.status.replace("{id}", String(params?.productId));
  return await axios.patch(path, { product: { status: params?.status } });
}

/* 
  RPP03. 공간상품 기본정보 등록
*/
export async function createProductAsync(
  axios: AxiosInstance,
  params?: {
    product: ProductAddModel;
  },
): Promise<AxiosResponse<ApiResponse<{ content?: string }>>> {
  return await axios.post(ApiPath.product.add.basicInfo, params);
}

/* 
  RPP04. 공간상품 정보 수정
*/
export async function updateProductAsync(
  axios: AxiosInstance,
  params?: {
    product: ProductEditModel;
  },
): Promise<AxiosResponse<ApiResponse<ProductUpdateResponseData>>> {
  if (!params?.product.id) {
    throw Error("id is not found.");
  }
  const path = ApiPath.product.edit.replace("{id}", params.product.id.toString());
  return await axios.put(path, params);
}

/* 
  RPP05. 공간상품 기본정보 상세
*/
export async function getProductDetailAsync(
  axios: AxiosInstance,
  params?: {
    productId: number;
  },
): Promise<AxiosResponse<ApiResponse<ProductDetailType>>> {
  if (!params?.productId) {
    throw Error("productId is not found.");
  }
  const path = ApiPath.product.detail.replace("{id}", params.productId.toString());
  return await axios.get(path);
}

/*
  RPP25. 내부 이용자 공개(일부 계약) 대상 계약 조회
*/
export async function getInternalMemberContractMapsAsync(
  axios: AxiosInstance,
  params?: {
    productId: number | string;
  },
): Promise<AxiosResponse<ApiResponse<{ allowedContractMap: InternalMemberContractMap[] }>>> {
  if (!params?.productId) {
    throw Error("productId is not found.");
  }
  const path = ApiPath.product.allowedContracts.replace("{productId}", String(params.productId));
  return await axios.get(path);
}

/*
  RPP26. 내부 이용자 공개(일부 계약) 대상 계약 등록/수정
*/
export async function postInternalMemberContractMapsAsync(
  axios: AxiosInstance,
  params?: {
    productId: number | string;
    internalMemberContractMap: InternalMemberContractMap[];
  },
): Promise<AxiosResponse<ApiResponse<{ isSuccess?: boolean }>>> {
  if (!params?.productId) {
    throw Error("productId is not found.");
  }
  const path = ApiPath.product.allowedContracts.replace("{productId}", String(params.productId));
  return await axios.post(path, { allowedContractMapList: params.internalMemberContractMap });
}

// RPP06. 공간상품 기본정보 삭제
export async function deleteProductsAsync(
  axios: AxiosInstance,
  params?: {
    productId: any;
  },
): Promise<AxiosResponse<ApiResponse<ProductUpdateResponseData>>> {
  const path = ApiPath.product.delete.replace("{id}", String(params?.productId));
  return await axios.delete(path);
}

// 공간상품 이미지 등록
export const postPrImageAsync = async (axios: AxiosInstance, parmas?: Array<MediaFile>): Promise<AxiosResponse<ApiResponse<Array<MediaFile>>>> => {
  return await axios.post("/api/ctrl/partners/{partnerId}/pr/mediaFile", parmas);
};

// RPM01. 공통 미디어파일 목록
export const getPrImageAsync = async (
  axios: AxiosInstance,
  params?: ImageRequestModal,
): Promise<AxiosResponse<ApiResponse<ProductMediaFileList>>> => {
  const imageParams = {
    serviceId: Number(params?.serviceId),
    mediaServiceType: String(params?.mediaServiceTypes.join()),
  };

  return axios.get(`/api/ctrl/partners/{partnerId}/pr/mediaFile`, { params: imageParams });
};

// 공간상품 이미지 삭제
export const deletePrImageAsync = async (
  axios: AxiosInstance,
  params?: {
    ids: Array<string>;
  },
) => {
  const { ids } = params || {};

  return axios.delete("/api/ctrl/partners/{partnerId}/pr/mediaFile", { data: { ids } });
};

/* 
  RPP22. 공간상품 빌딩 공용공간 등록/수정/삭제
*/
export async function mutationProductBuildingCommonFacility(
  axios: AxiosInstance,
  params?: {
    productBuildingCommonFacilitySaveList: ProductBuildingCommonFacilitySaveListReq;
    productId: string | number;
  },
): Promise<AxiosResponse<ApiResponse<ProductBuildingCommonFacilitySaveListRes>>> {
  if (!params?.productId) {
    throw Error("id is not found.");
  }

  const path = ApiPath.product.productBuildingCommonFacility.replace("{id}", params.productId.toString());
  return await axios.post(path, { productBuildingCommonFacilitySaveList: params.productBuildingCommonFacilitySaveList });
}

/* 
  RPB12. 공간상품 빌딩 조회
*/
export async function buildingsDetails(
  axios: AxiosInstance,
  params?: {
    building: {
      ids: string[];
    };
  },
): Promise<AxiosResponse<ApiResponse<{ content?: [{ building: BuildingModel }] }>>> {
  const path = ApiPath.building.details;
  return await axios.post(path, params);
}

export interface AvailableReservationTime {
  id?: number; // 예약가능시간대 ID
  serviceId?: number; // 서비스ID
  serviceType?: UnionServiceType; // 서비스타입
  commonFacilityType?: SpaceTypeT; // 공용공간타입
  isDefault?: boolean; // 기본설정여부
  buildingCommonFacilityId?: number; // 건물-공용공간 ID (isDefault=false인 경우 필수)
  isLimitedAvailableReservationTime?: boolean; // 예약가능시간대제한여부
  availableReservationTimeStart?: number; // 예약가능시간대시작 (단위: 분)
  availableReservationTimeEnd?: number; // 예약가능시간대종료
  cmdType?: UnionCmdType; // 요청타입
  partnerId?: number; // 파트너ID
}

export interface AvailableReservationTimeListParams {
  page?: number;
  size?: number;
  sort?: Sort;
  serviceId?: string; // 사용하고자 하는 서비스들의 pk (등록시 필수, 수정불가)
  serviceType?: UnionServiceType; // 사용하고자 하는 서비스들의 타입 (등록시 필수, 수정불가)
  isDefault?: boolean;
  commonFacilityType?: SpaceTypeT;
  buildingCommonFacilityId?: string; // 건물-공용공간 ID (isDefault=false인 경우 필수)
}

export interface UnitReservationTime {
  id?: number; // 예약단위 ID (수정 시 필수)
  serviceId?: number; // 서비스ID
  serviceType?: UnionServiceType; // 서비스타입
  commonFacilityType?: SpaceTypeT; // 공용공간타입
  isDefault?: boolean; // 기본설정여부
  buildingCommonFacilityId?: number; // 건물-공용공간 ID (isDefault=true인 경우 필수)
  isLimitedUnitReservationTime?: boolean; // 예약단위제한여부
  minimumReservationTime?: number; // 최소예약시간 (단위: 분, 제한없음: -1)
  maximumReservationTime?: number; // 최대예약시간
  cmdType?: UnionCmdType; // 요청타입
  partnerId?: number; // 파트너ID
}

export interface UnitReservationTimeListParams {
  page?: number;
  size?: number;
  sort?: Sort;
  serviceId?: string; // 사용하고자 하는 서비스들의 pk (등록시 필수, 수정불가)
  serviceType?: UnionServiceType; // 사용하고자 하는 서비스들의 타입 (등록시 필수, 수정불가)
  isDefault?: boolean;
  commonFacilityType?: SpaceTypeT;
  buildingCommonFacilityId?: string; // 건물-공용공간 ID (isDefault=false인 경우 필수)
}

export interface ReservationPolicyModeListParams {
  page?: number;
  size?: number;
  sort?: Sort;
  serviceId?: string; // 사용하고자 하는 서비스들의 pk (등록시 필수, 수정불가)
  serviceType?: UnionServiceType; // 사용하고자 하는 서비스들의 타입 (등록시 필수, 수정불가)
  commonFacilityType?: SpaceTypeT;
  buildingCommonFacilityId?: string; // 건물-공용공간 ID (isDefault=false인 경우 필수)
  reservationPolicyModeType?: ReservationPolicyModeType;
}

// export const reservationPolicyModeTypeMap = {
//   RESERVATION_POLICY_MODE_UNRECOGNIZED: "정의되지 않은 상태",
//   RESERVATION_POLICY_MODE_AVAILABLE: "예약가능구간",
//   RESERVATION_POLICY_MODE_UNIT: "예약시간단위",
//   RESERVATION_POLICY_MODE_CLOSED_PERIOD: "휴무일",
//   RESERVATION_POLICY_MODE_START_TIME: "예약시작시간",
// } as const;
export const reservationPolicyModeTypeMap = {
  RESERVATION_POLICY_MODE_UNRECOGNIZED: "RESERVATION_POLICY_MODE_UNRECOGNIZED", // 정의되지 않은 상태
  RESERVATION_POLICY_MODE_AVAILABLE: "RESERVATION_POLICY_MODE_AVAILABLE", // 예약가능구간
  RESERVATION_POLICY_MODE_UNIT: "RESERVATION_POLICY_MODE_UNIT", // 예약시간단위
  RESERVATION_POLICY_MODE_CLOSED_PERIOD: "RESERVATION_POLICY_MODE_CLOSED_PERIOD", // 휴무일
  RESERVATION_POLICY_MODE_START_TIME: "RESERVATION_POLICY_MODE_START_TIME", // 예약시작시간
} as const;

export type ReservationPolicyModeType = keyof typeof reservationPolicyModeTypeMap;

export interface ReservationPolicyMode {
  id?: number; // 예약단위 ID (수정 시 필수)
  serviceId?: number; // 서비스ID
  serviceType?: UnionServiceType; // 서비스타입
  commonFacilityType?: SpaceTypeT; // 공용공간타입
  isDefault?: boolean; // 기본설정여부
  buildingCommonFacilityId?: number; // 건물-공용공간 ID (isDefault=true인 경우 필수)
  reservationPolicyModeType?: ReservationPolicyModeType;
  cmdType?: UnionCmdType; // 요청타입
  partnerId?: number; // 파트너ID
}

export interface ReservationPolicyClosedPeriod {
  id?: number; // 예약단위 ID (수정 시 필수)
  serviceId?: number; // 서비스ID
  serviceType?: UnionServiceType; // 서비스타입
  commonFacilityType?: SpaceTypeT; // 공용공간타입
  isDefault?: boolean; // 기본설정여부
  buildingCommonFacilityId?: number; // 건물-공용공간 ID (isDefault=false인 경우 필수)
  days?: string; // 요일 (콤마로 구분, 지정하지 않을 경우 빈 값)
  startDate?: string; // 시작일시 (예: 2024-01-01T00:00:00+09:00)
  endDate?: string; // 종료일시 (예: 2024-01-31T00:00:00+09:00)
  startTime?: string; // 시작시간 (30분 단위, 예: 00:00)
  endTime?: string; // 종료시간 (30분 단위, 예: 24:00)
  cmdType?: UnionCmdType; // 요청타입
  partnerId?: number; // 파트너ID
}

export interface ReservationPolicyClosedPeriodListParams {
  page?: number;
  size?: number;
  sort?: Sort;
  serviceId?: string; // 사용하고자 하는 서비스들의 pk (등록시 필수, 수정불가)
  serviceType?: UnionServiceType; // 사용하고자 하는 서비스들의 타입 (등록시 필수, 수정불가)
  commonFacilityType?: SpaceTypeT;
  isDefault?: boolean; // 기본설정여부
  buildingCommonFacilityId?: number; // 건물-공용공간 ID (isDefault=false인 경우 필수)
}

/* 
  RES01. 예약가능구간 설정 등록/수정
  https://pnpt.atlassian.net/wiki/x/EICcLQ
*/
export async function mutationAvailableReservationTimeAsync(
  axios: AxiosInstance,
  params?: AvailableReservationTime[],
): Promise<AxiosResponse<ApiResponse<{ content?: AvailableReservationTime[] }>>> {
  return await axios.post(ApiPath.product.availableReservationTime, {
    availableReservationTimeList: params,
  });
}

/**
 * RES03. 예약가능구간 설정값 DB 조회
 * https://pnpt.atlassian.net/wiki/x/PwCiLQ
 */
export async function getAvailableReservationTimeAsync(
  axios: AxiosInstance,
  params?: AvailableReservationTimeListParams,
): Promise<AxiosResponse<ApiResponse<{ content?: AvailableReservationTime[] }>>> {
  return await axios.get(ApiPath.product.availableReservationTime, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/* 
  RES04. 예약시간단위 설정 등록/수정
  https://pnpt.atlassian.net/wiki/x/AgCiLQ
*/
export async function mutationUnitReservationTimeAsync(
  axios: AxiosInstance,
  params?: UnitReservationTime[],
): Promise<AxiosResponse<ApiResponse<{ content?: UnitReservationTime[] }>>> {
  return await axios.post(ApiPath.product.unitReservationTime, {
    unitReservationTimeList: params,
  });
}

/**
  RES06. 예약시간단위 설정값 DB 조회
  https://pnpt.atlassian.net/wiki/x/ZACgLQ
 */
export async function getUnitReservationTimeAsync(
  axios: AxiosInstance,
  params?: AvailableReservationTimeListParams,
): Promise<AxiosResponse<ApiResponse<{ content?: UnitReservationTime[] }>>> {
  return await axios.get(ApiPath.product.unitReservationTime, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/* 
  RES07. 개별예약정책 설정 방식 등록/수정
  https://pnpt.atlassian.net/wiki/x/KACgLQ
*/
export async function mutationReservationPolicyModeAsync(
  axios: AxiosInstance,
  params?: ReservationPolicyMode[],
): Promise<AxiosResponse<ApiResponse<{ content?: ReservationPolicyMode[] }>>> {
  return await axios.post(ApiPath.product.reservationPolicyMode, {
    reservationPoliyModeList: params,
  });
}

/**
  RES08. 개별예약정책 설정 방식 정보 DB 조회
  https://pnpt.atlassian.net/wiki/x/KgCiLQ
 */
export async function getReservationPolicyModeAsync(
  axios: AxiosInstance,
  params?: ReservationPolicyModeListParams,
): Promise<AxiosResponse<ApiResponse<{ content?: ReservationPolicyMode[] }>>> {
  return await axios.get(ApiPath.product.reservationPolicyMode, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/*
  RES09. 휴무일 설정 등록/수정/삭제
  https://pnpt.atlassian.net/wiki/x/OwCgLQ

  @deprecated MVDV-9724 로 deprecated. rv 기반 호환 구현이라 휴무 사유(title)를 지원하지 않는다.
  대신 addClosingRecurrenceAsync / editClosingRecurrenceAsync / deleteClosingRecurrenceAsync 사용.
*/
export async function mutationReservationPolicyClosedPeriodAsync(
  axios: AxiosInstance,
  params?: ReservationPolicyClosedPeriod[],
): Promise<AxiosResponse<ApiResponse<{ content?: ReservationPolicyClosedPeriod[] }>>> {
  return await axios.post(ApiPath.product.reservationPolicyClosedPeriod, {
    reservationPolicyClosedPeriodList: params,
  });
}

/**
  RES10. 휴무일 설정값 DB 조회
  https://pnpt.atlassian.net/wiki/x/TgCgLQ

  @deprecated MVDV-9724 로 deprecated. rv 기반 호환 구현이라 휴무 사유(title)를 내려주지 않는다.
  대신 getReservationPolicyListAsync(policyType: "closing-time-policy") 사용.
 */
export async function getReservationPolicyClosedPeriodAsync(
  axios: AxiosInstance,
  params?: ReservationPolicyClosedPeriodListParams,
): Promise<AxiosResponse<ApiResponse<{ content?: ReservationPolicyClosedPeriod[] }>>> {
  return await axios.get(ApiPath.product.reservationPolicyClosedPeriod, {
    params,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

/* ------------------------------------------------------------------
  공용공간 예약 정책 rv API (RRVP03~14)
  deprecated 된 RRVP01/RRVP02 를 대체한다.
  상품/계약은 경로만 다르고 요청/응답 형태가 같아 한 함수에서 분기한다.
  owner 로 productId 를 주면 상품 API, contractManageId 를 주면 계약 API 를 호출한다.
------------------------------------------------------------------ */

// owner(상품/계약)에 따라 경로를 고르고 path variable 을 치환한다.
function resolveReservationPolicyPath(owner: ReservationPolicyOwner, paths: { product: string; contract: string }, suffix = ""): string {
  if (owner.productId !== undefined && owner.productId !== null && owner.productId !== "") {
    return paths.product.replace("{productId}", String(owner.productId)) + suffix;
  }
  if (owner.contractManageId !== undefined && owner.contractManageId !== null && owner.contractManageId !== "") {
    return paths.contract.replace("{contractManageId}", String(owner.contractManageId)) + suffix;
  }
  throw Error("productId or contractManageId is not found.");
}

const RESERVATION_POLICY_PATHS = {
  product: ApiPath.product.reservationPolicy.productPolicies,
  contract: ApiPath.product.reservationPolicy.contractPolicies,
};

const RESERVATION_RECURRENCE_PATHS = {
  product: ApiPath.product.reservationPolicy.productRecurrences,
  contract: ApiPath.product.reservationPolicy.contractRecurrences,
};

/**
  RRVP03/09. 공용 공간 예약 정책 목록 조회
  https://pnpt.atlassian.net/wiki/x/SoCsjQ (상품) / https://pnpt.atlassian.net/wiki/x/AYC8jQ (계약)
 */
export async function getReservationPolicyListAsync(
  axios: AxiosInstance,
  params: ReservationPolicyListParams,
): Promise<AxiosResponse<ReservationPolicyPage & { data?: ReservationPolicyPage }>> {
  const { productId, contractManageId, ...query } = params;
  const path = resolveReservationPolicyPath({ productId, contractManageId }, RESERVATION_POLICY_PATHS);

  return await axios.get(path, {
    params: query,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { allowDots: true, encode: true }),
    },
  });
}

// RRVP03/09 는 pageSize 최대 100 이라 RRVP01 처럼 한 번에 다 받을 수 없다.
// 상품/계약 하나의 정책을 전부 모아야 목록·요약을 그릴 수 있어 페이지를 끝까지 훑는다.
const RESERVATION_POLICY_PAGE_SIZE = 100;
const RESERVATION_POLICY_MAX_PAGE = 20;

/** RRVP03/09 를 끝까지 페이징해 정책 전체를 모은다. */
export async function getAllReservationPolicyListAsync(axios: AxiosInstance, params?: ReservationPolicyListParams): Promise<ReservationPolicyModel[]> {
  if (!params) throw Error("reservation policy params is not found.");
  const policies: ReservationPolicyModel[] = [];

  for (let pageNumber = 0; pageNumber < RESERVATION_POLICY_MAX_PAGE; pageNumber++) {
    const response = await getReservationPolicyListAsync(axios, { ...params, pageNumber, pageSize: RESERVATION_POLICY_PAGE_SIZE });
    const body = response?.data;
    // rv 정책 API 는 { policies, pagination } 을 루트로 내려준다(et 의 { data, meta } 래퍼 없음).
    // 래핑되는 환경이 있을 수 있어 data 아래도 함께 본다.
    const page = body?.policies ?? body?.data?.policies ?? body?.content ?? body?.data?.content ?? [];

    policies.push(...page);
    if (page.length < RESERVATION_POLICY_PAGE_SIZE) break;

    // 서버가 pageNumber 를 무시하면 같은 페이지를 반복해서 받게 되므로 전체 건수로 한 번 더 끊는다.
    // 빈 문자열은 Number("") === 0 이라 첫 페이지에서 멈춰버리므로 건수 없음으로 본다
    const rawTotalSize = body?.pagination?.totalSize ?? body?.data?.pagination?.totalSize;
    const totalSize = rawTotalSize === "" || rawTotalSize == null ? NaN : Number(rawTotalSize);
    if (Number.isFinite(totalSize) && policies.length >= totalSize) break;

    if (pageNumber === RESERVATION_POLICY_MAX_PAGE - 1) {
      console.warn(`[RRVP03/09] 정책이 ${RESERVATION_POLICY_MAX_PAGE * RESERVATION_POLICY_PAGE_SIZE}건을 넘어 뒤쪽이 잘렸습니다.`);
    }
  }

  return policies;
}

// 정책 대상 키. 개별 공간 정책이면 facilityId, 타입 기본 정책이면 facilityType (oneof)
function resolveReservationPolicyTarget(params: ReservationPolicyTarget) {
  const { facilityType, facilityId } = params;

  if (facilityId !== undefined && facilityId !== null && facilityId !== "") return { facilityId: String(facilityId) };
  if (facilityType) return { facilityType };

  // 둘 다 없으면 서버가 대상 정책을 못 찾는다. 빈 값으로 보내지 않고 여기서 끊는다.
  throw Error("facilityId or facilityType is not found.");
}

/**
  RRVP04/10. 공용 공간 예약 정책 저장
  https://pnpt.atlassian.net/wiki/spaces/MVPD/pages/2376990801 (상품) / https://pnpt.atlassian.net/wiki/spaces/MVPD/pages/2377941008 (계약)

  RRVP02 와 달리 { key, details } 중첩 없이 대상·정책이 body 최상위로 평탄화된다.
  적용 방식(exclusive)은 이 API 가 아니라 RRVP05/11 로 저장한다.
 */
export async function saveReservationPolicyAsync(
  axios: AxiosInstance,
  params?: ReservationPolicySaveParams,
): Promise<AxiosResponse<{ policy?: ReservationPolicyModel } & { data?: { policy?: ReservationPolicyModel } }>> {
  if (!params) throw Error("reservation policy params is not found.");
  const { productId, contractManageId, facilityType, facilityId, startTimePolicy, usageLimitPolicy } = params;
  const path = resolveReservationPolicyPath({ productId, contractManageId }, RESERVATION_POLICY_PATHS);

  return await axios.put(path, {
    ...resolveReservationPolicyTarget({ facilityType, facilityId }),
    // policyType 필드는 없다. 어떤 정책 객체를 보냈는지로 서버가 판단하므로 넘어온 것만 담는다
    ...(startTimePolicy && { startTimePolicy }),
    ...(usageLimitPolicy && { usageLimitPolicy }),
  });
}

/**
  RRVP05/11. 공용 공간 정책 적용 방식(기본/개별) 저장
  https://pnpt.atlassian.net/wiki/x/FwCdjQ (상품) / https://pnpt.atlassian.net/wiki/x/AYC_jQ (계약)

  개별 공간에만 의미가 있어 facilityId 가 필수다(기본 정책 화면에는 적용 방식 선택이 없다).
 */
export async function saveReservationPolicyExclusiveAsync(
  axios: AxiosInstance,
  params?: ReservationPolicyExclusiveParams,
): Promise<AxiosResponse<{ policy?: ReservationPolicyModel } & { data?: { policy?: ReservationPolicyModel } }>> {
  if (!params) throw Error("reservation policy exclusive params is not found.");
  const { productId, contractManageId, policyType, facilityId, exclusive } = params;
  const path = resolveReservationPolicyPath({ productId, contractManageId }, { product: ApiPath.product.reservationPolicy.productExclusive, contract: ApiPath.product.reservationPolicy.contractExclusive });

  return await axios.post(path, {
    policyType,
    facilityId: String(facilityId),
    exclusive,
  });
}

// 휴무일 추가/수정 body (경로로 소유자와 대상이 정해지므로 body 에는 휴무 값만 담는다)
function toClosingRecurrenceBody(params: ClosingRecurrenceAddParams) {
  const { title, effectiveFrom, effectiveTo, daysOfWeek, unavailableFrom, unavailableUntil } = params;

  return {
    ...resolveReservationPolicyTarget(params),
    title,
    effectiveFrom,
    effectiveTo,
    daysOfWeek,
    unavailableFrom,
    unavailableUntil,
  };
}

/**
  RRVP06/12. 휴무일 추가
  https://pnpt.atlassian.net/wiki/x/UYDAS (상품) / https://pnpt.atlassian.net/wiki/x/H4C8jQ (계약)
 */
export async function addClosingRecurrenceAsync(
  axios: AxiosInstance,
  params: ClosingRecurrenceAddParams,
): Promise<AxiosResponse<ApiResponse<{ policy?: ReservationPolicyModel }>>> {
  const path = resolveReservationPolicyPath(params, RESERVATION_RECURRENCE_PATHS);

  return await axios.post(path, toClosingRecurrenceBody(params));
}

/**
  RRVP07/13. 휴무일 수정
  https://pnpt.atlassian.net/wiki/x/RACJj (상품) / https://pnpt.atlassian.net/wiki/x/EIC_jQ (계약)
 */
export async function editClosingRecurrenceAsync(
  axios: AxiosInstance,
  params: ClosingRecurrenceEditParams,
): Promise<AxiosResponse<ApiResponse<{ policy?: ReservationPolicyModel }>>> {
  const path = resolveReservationPolicyPath(params, RESERVATION_RECURRENCE_PATHS, `/${params.id}`);

  return await axios.put(path, toClosingRecurrenceBody(params));
}

/**
  RRVP08/14. 휴무일 삭제
  https://pnpt.atlassian.net/wiki/x/DoDLS (상품) / https://pnpt.atlassian.net/wiki/x/LoC8jQ (계약)
 */
export async function deleteClosingRecurrenceAsync(axios: AxiosInstance, params: ClosingRecurrenceDeleteParams): Promise<AxiosResponse<ApiResponse<null>>> {
  const path = resolveReservationPolicyPath(params, RESERVATION_RECURRENCE_PATHS, `/${params.id}`);

  return await axios.delete(path, {
    params: resolveReservationPolicyTarget(params),
  });
}
